import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  accountType: 'founder' | 'group_a' | 'owner';
  profilePictureUrl?: string;
  phoneNumber?: string;
  city?: string;
  title?: string;
  equityPercentage?: number;
  isTechnicalFounder?: boolean;
  workHistory?: string;
  education?: string;
  country?: string;
  postCode?: string;
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  register: (userData: any) => Promise<{ success: boolean; error?: string }>;
  login: (credentials: any) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  verifyEmail: (token: string) => Promise<{ success: boolean; error?: string }>;
  resendVerification: (email: string) => Promise<{ success: boolean; error?: string }>;
  updateProfile: (profileData: any) => Promise<{ success: boolean; error?: string }>;
  uploadProfilePicture: (file: File) => Promise<{ success: boolean; error?: string; profilePictureUrl?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Set up axios defaults
  axios.defaults.baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  // Add token to requests if it exists
  axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle token expiration
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setUser(null);
        // toast.error('Session expired. Please login again.');
      }
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token and get user data
      verifyToken();
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get('/users/profile');
      setUser(response.data.user);
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: any) => {
    try {
      const response = await axios.post('/auth/register', userData);
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      setUser(user);
      
      toast.success('Registration successful! Please check your email to verify your account.');
      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const login = async (credentials: any) => {
    try {
      const response = await axios.post('/auth/login', credentials);
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      setUser(user);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error: any) {
      console.log(error);
      const message = error.response?.data?.error || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast.success('Logged out successfully');
  };

  const verifyEmail = async (token: string) => {
    try {
      await axios.post('/auth/verify-email', { token });
      
      // Refresh user data to get updated verification status
      const response = await axios.get('/users/profile');
      setUser(response.data.user);
      
      toast.success('Email verified successfully!');
      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Email verification failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const resendVerification = async (email: string) => {
    try {
      await axios.post('/auth/resend-verification', { email });
      toast.success('Verification email sent successfully!');
      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to send verification email';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateProfile = async (profileData: any) => {
    try {
      const response = await axios.put('/users/profile', profileData);
      setUser(response.data.user);
      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update profile';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const uploadProfilePicture = async (file: File) => {
    const formData = new FormData();
    formData.append('profilePicture', file);

    try {
      const response = await axios.post('/users/profile-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 second timeout
      });
      
      // Use the dataUrl directly for immediate display
      const dataUrl = response.data.dataUrl;
      
      // Update the user state with the new profile picture URL (data URL)
      if (user) {
        setUser({
          ...user,
          profilePictureUrl: dataUrl
        });
      }
      
      toast.success('Profile picture uploaded successfully!');
      return { success: true, profilePictureUrl: dataUrl };
    } catch (error: any) {
      console.error('Upload profile picture error:', error);
      
      // More specific error messages
      let message = 'Failed to upload profile picture';
      if (error.code === 'ECONNABORTED') {
        message = 'Upload timed out. Please try again.';
      } else if (error.response?.status === 413) {
        message = 'File too large. Please select a smaller image.';
      } else if (error.response?.data?.error) {
        message = error.response.data.error;
      }
      
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    register,
    login,
    logout,
    verifyEmail,
    resendVerification,
    updateProfile,
    uploadProfilePicture,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 